/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Deva Sen., Ryan Duffy & Warren Dowey
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { merge, Subscription } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { Location } from '@angular/common';
import { environment } from 'environments/environment';
import { SubSink } from '@base/node_modules/subsink/dist/subsink';
import { ComponentPermissionsListService } from '@app/services/api-services/component-permissions/component-permissions-list.service';
import { ApiParamsModel } from '@app/models/api-params.model';
import { Logger } from '@app/services/logger/logger.service';
import { ParameterStoreService } from '@app/services/aws-services/parameter-store.service';
import { AuthenticationService } from '@app/services/authentication/authentication.service';
import { I18nService } from '@app/services/i18n/i18n.service';
import { MaterialSnackbarService } from '@app/services/shared-services/material-snackbar.service';
import { ApiService } from './../../../../src/app/services/api-services/api.service';
import { OrganisationUserService } from '@app/services/api-services/organisation-users/organisation-user.service';
import { OrganisationUsersModel } from '@app/models/organisation-users.model';
const log = new Logger('AppComponent');
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private userSubscription: Subscription;
  // Subsink to be used in the OnDestroy Lifecycle hook to destroy all active subscriptions
  private subscriptionContainer = new SubSink();
  protected user: OrganisationUsersModel;
  maintenanceMode;
  sonrai_super_user;

  /**
   * Creates an instance of AppComponent.
   *
   * @constructor
   * @param {Router} router
   * @param {ActivatedRoute} activatedRoute
   * @param {Title} titleService
   * @param {TranslateService} translateService
   * @param {I18nService} i18nService
   * @param {Location} location
   * @param {AuthenticationService} authenticationService
   * @param {ComponentPermissionsListService} componentPermissionsListService
   * @param {ParameterStoreService} parameterStoreService
   * @param {AuthenticationService} authService
   * @param {MaterialSnackbarService} notify
   * @param {ApiService} api
   * @param {OrganisationUserService} organisationUserService
   */
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public titleService: Title,
    private translateService: TranslateService,
    private i18nService: I18nService,
    public location: Location,
    public authenticationService: AuthenticationService,
    public componentPermissionsListService: ComponentPermissionsListService,
    private parameterStoreService: ParameterStoreService,
    private authService: AuthenticationService,
    private notify: MaterialSnackbarService,
    private api: ApiService,
    private organisationUserService: OrganisationUserService,
  ) {
    // Check maintenanceMode value every minute
    setInterval(()=> {
      // Check if maintenance mode is active, don't perform check if user is not logged in
      if(sessionStorage.credentials != null) {
        this.checkUserAndMaintenanceMode()
      }
    }, 300000);
  }
  /**
   * @desciption sets the title and routing for the application overall
   * Also, checks upon successful navigation if the current session is the correct user session and refresh the user token
   *
   * @memberof AppComponent
   */
  ngOnInit() {
    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }
    log.debug('init');
    // Setup translations
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);
    const onNavigationEnd = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    );
    // Change page title on navigation or language change, based on route data
    this.subscriptionContainer.sink = merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe((event) => {
        // Check if cookies are enabled
        let cookiesEnabled = navigator.cookieEnabled;
        if(!cookiesEnabled) {
          this.notify.openSnackBar('Warning cookies are not enabled, this can result in certain features failing.', 'Close');
        }
        // Check if maintenance mode is active, don't perform check if user is not logged in
        if(sessionStorage.credentials != null) {
          this.checkUserAndMaintenanceMode();
        }
        const title = event['title'];
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });
    if (this.authService.user) {
      // this.componentPermissionsListService.getData(new ApiParamsModel());
    }
  }

  /**
   * TODO Description placeholder
   */
  checkUserAndMaintenanceMode() {
    if(this.sonrai_super_user != null) {
      this.checkMaintenanceMode();
    } else {
      // Only getOrganisationUserByUserId if sonrai_super_user value isn't already set
      let user = this.authService.user;
      this.subscriptionContainer.sink = this.userSubscription = this.organisationUserService.getOrganisationUserByUserId(user.userId).subscribe(
        (userData) => {
          this.user = userData;
          this.sonrai_super_user = this.user.sonraiSuperUser;
          this.checkMaintenanceMode();
          if (this.userSubscription) {
            this.userSubscription.unsubscribe();
          }
        }
      );
    }
  }

  /**
   * TODO Description placeholder
   */
  checkMaintenanceMode() {
    this.subscriptionContainer.sink = this.parameterStoreService
      .getParameter(environment.maintenanceMode)
      .subscribe((data) => {
          this.maintenanceMode = data.Parameter.Value;
          // Check maintenanceMode and if user is a super user
          if(this.maintenanceMode === 'true' && this.sonrai_super_user === false) {
            // Redirect to maintenance page if user is not already on the maintenance page and if user is not a super user
            if(this.router.url !== '/maintenance') {
              this.router.navigate(['/maintenance'])
            }
          }
          else {
          if((this.maintenanceMode === 'false') || (this.sonrai_super_user === true)) {
            // Redirect user back to login page if maintenance mode is no longer active
            if(this.router.url === '/maintenance') {
              this.router.navigate(['/login'])
            }
          }
        }
      });
  }

  /**
   * TODO Description placeholder
   */
  ngOnDestroy() {
    this.subscriptionContainer.unsubscribe();
  }
}
